/**
 * Brand house hero — scroll reveal.
 *
 * A narrow, soft-edged diagonal beam of transparency (see .bh-hero__cover in
 * brand-house.css) sweeps across a darkened photo mosaic as the visitor
 * scrolls the hero out of view -- not a wipe/curtain, a beam: only the strip
 * currently under it is revealed, and the mosaic goes back to fully covered
 * once the beam has passed. The cover is a plain div with a CSS mask
 * gradient, not a stretched SVG shape -- a previous version used a wavy
 * bezier curve stretched to cover a tall buffer, which distorted into
 * jagged zigzags on narrow viewports (scaleX and scaleY diverged by ~20x on
 * mobile). A gradient mask has no such failure mode: its angle is a true
 * geometric angle, not a shape that gets stretched, so sliding its position
 * is safe at any hero height or screen width, and only needs one number:
 * `progress`, applied to both mask-position axes together so the beam
 * travels diagonally from the top-left toward the bottom-right.
 *
 * Progress is 0 while the hero still fills the viewport (nothing revealed
 * yet) and reaches 1 once the hero has fully scrolled past.
 *
 * No scroll hijacking, native scrolling untouched. Disabled entirely under
 * reduced-motion (matches brand-house.css, which hides the layers outright
 * for that case) so there's nothing to compute or listen for.
 */
(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  // mask-size and mask-position are set here (in px) rather than in CSS
  // (in %): mask-position percentages against an oversized mask image
  // turned out to clamp into hard, non-diagonal edges at some offsets --
  // verified empirically. Sizing the mask to MASK_SCALE times the hero's
  // own measured width/height and positioning it in px sidesteps that
  // entirely. At position (0, 0) the visible window sits in the mask
  // image's top-left, showing the gradient's opaque start (rest -- beam
  // not yet reached). At (-rect.width, -rect.height) -- i.e. MASK_SCALE=2,
  // so exactly one hero-width/height of travel -- the window sits in the
  // image's bottom-right, showing the opaque end (full reveal -- beam has
  // passed through and moved on). The transparent band in between sweeps
  // across the diagonal as progress goes from 0 to 1.
  var MASK_SCALE = 2;

  // Progress would otherwise take the hero's full scroll-past distance
  // (one hero height) to go from 0 to 1. Multiplying it speeds that up --
  // the beam completes its sweep after scrolling only 1/SPEED of the
  // hero's height, then holds at the fully-swept end state for the rest
  // of the scroll-past.
  var SPEED = 2.2;

  // Matches assets/scroll-container.js: in the desktop "squeeze" layout,
  // .page-wrapper is the real scrolling element (document.scrollingElement
  // has overflow: hidden and never scrolls), so `window`/`document` never
  // fire 'scroll' there. Below 990px the document scrolls natively. Scroll
  // events on .page-wrapper don't bubble to window, so we must listen on
  // whichever element is actually scrolling, and rebind if the breakpoint
  // changes underneath us (e.g. rotating a tablet).
  var SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

  function getScrollEventTarget() {
    if (SQUEEZE_QUERY.matches) {
      return document.querySelector('.page-wrapper') || document;
    }
    return document;
  }

  function Wave(hero) {
    this.hero = hero;
    this.cover = hero.querySelector('.bh-hero__cover');
    this.ticking = false;
    this.target = null;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
    this.bind = this.bind.bind(this);

    this.bind();
    SQUEEZE_QUERY.addEventListener('change', this.bind);
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  Wave.prototype.bind = function () {
    var next = getScrollEventTarget();
    if (next === this.target) return;
    if (this.target) this.target.removeEventListener('scroll', this.onScroll);
    this.target = next;
    this.target.addEventListener('scroll', this.onScroll, { passive: true });
  };

  Wave.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.update);
  };

  Wave.prototype.update = function () {
    var rect = this.hero.getBoundingClientRect();
    var progress = Math.min(Math.max((-rect.top / rect.height) * SPEED, 0), 1);

    var maskSize = rect.width * MASK_SCALE + 'px ' + rect.height * MASK_SCALE + 'px';
    this.cover.style.maskSize = maskSize;
    this.cover.style.webkitMaskSize = maskSize;

    // progress 0 -> (-width, -height): window in the mask's top-left,
    // beam just entering. progress 1 -> (0, 0): window in the mask's
    // bottom-right, beam has swept through and exited.
    var remaining = 1 - progress;
    var maskPos = -rect.width * remaining + 'px ' + -rect.height * remaining + 'px';
    this.cover.style.maskPosition = maskPos;
    this.cover.style.webkitMaskPosition = maskPos;

    this.ticking = false;
  };

  Wave.prototype.destroy = function () {
    if (this.target) this.target.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    SQUEEZE_QUERY.removeEventListener('change', this.bind);
  };

  var instances = [];

  function initAll(scope) {
    if (window.matchMedia(REDUCED).matches) return;
    (scope || document).querySelectorAll('[data-wave-reveal]').forEach(function (hero) {
      instances.push(new Wave(hero));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var hero = e.target.querySelector('[data-wave-reveal]');
    if (!hero) return;
    instances = instances.filter(function (instance) {
      if (instance.hero === hero) {
        instance.destroy();
        return false;
      }
      return true;
    });
  });
})();
